import { Link } from "react-router-dom";
import { Building2, Activity, Users, ArrowRight } from "lucide-react";
import PageHero from "@/components/PageHero";
import CtaBand from "@/components/CtaBand";
import FacilityCarousel from "@/components/FacilityCarousel";
import { IMAGES } from "@/lib/data";

export default function About() {
  return (
    <div data-testid="about-page">
      <PageHero
        eyebrow="About ZT Health"
        title="Healthcare Investment for Good"
        subtitle="ZT Health is built on the principles of physician empowerment, operational efficiency, and community-based healthcare, designed to reduce inefficiencies in traditional systems and help care teams focus on what matters most: patients."
      />

      {/* Facility carousel — sits directly below PageHero, above the narrative pod */}
      <section
        data-testid="facility-carousel-section"
        className="pt-8 md:pt-12 pb-4 md:pb-6"
        style={{ background: "var(--zt-bg-alt)" }}
      >
        <div className="zt-container">
          <FacilityCarousel />
        </div>
      </section>

      {/* About narrative */}
      <section className="zt-section">
        <div className="zt-container">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-16 items-start">
            <div className="lg:col-span-7">
              <h2 className="text-3xl md:text-4xl font-bold leading-tight mb-6">
                A multi-vertical healthcare platform serving more than 150,000 patients each year
              </h2>
              <p className="text-lg text-slate-600 leading-relaxed">
                Over the years, ZT Health has expanded through strategic development, acquisitions, and operational consolidation, including the growth of Altus Emergency Centers, the conversion of Baytown Surgery Center into Altus Baytown Hospital, and the acquisitions of Neighbors Emergency Centers, Austin ERs, Exceptional Emergency Centers, Horizon Health Partners, and Express ER facilities.
              </p>
              <p className="text-lg text-slate-600 leading-relaxed mt-5">
                Today, ZT Health serves more than 150,000 patients each year through a multi-vertical platform designed to improve access, strengthen clinical coverage, and deliver scalable community-based healthcare.
              </p>
            </div>
            <div className="lg:col-span-5">
              <div className="relative">
                <img
                  src={IMAGES.physicianPatient}
                  alt="Physician with patient"
                  className="w-full h-[460px] object-cover"
                />
                <div
                  className="absolute -bottom-5 -right-5 w-28 h-28 -z-10"
                  style={{ background: "var(--zt-navy)" }}
                />
                <div
                  className="absolute -top-5 -left-5 w-16 h-16 -z-10"
                  style={{ background: "var(--zt-teal)" }}
                />
              </div>
            </div>
          </div>

          {/* Enlarged stat boxes — always 3 across (side-by-side on mobile too) */}
          <div className="grid grid-cols-3 gap-3 md:gap-6 mt-12 md:mt-16" data-testid="about-stats">
            {[
              { icon: Building2, value: "27", label: "Facilities" },
              { icon: Activity, value: "150K+", label: "Patients / Yr" },
              { icon: Users, value: "1,200+", label: "Employees" },
            ].map((s) => {
              const Icon = s.icon;
              return (
                <div
                  key={s.label}
                  data-testid={`about-stat-${s.label.toLowerCase().replace(/[\s/]+/g, "-")}`}
                  className="bg-white border-l-4 md:border-l-8 border-t border-r border-b border-slate-200 p-4 md:p-8 lg:p-10 group hover:shadow-[0_16px_50px_rgba(15,23,42,0.10)] hover:-translate-y-1 transition-all duration-300"
                  style={{ borderLeftColor: "var(--zt-teal)" }}
                >
                  <Icon size={20} className="md:hidden" style={{ color: "var(--zt-navy)" }} />
                  <Icon size={28} className="hidden md:block" style={{ color: "var(--zt-navy)" }} />
                  <p
                    className="text-2xl sm:text-3xl md:text-5xl lg:text-6xl font-bold mt-2 md:mt-4 leading-none"
                    style={{ color: "var(--zt-navy)", fontFamily: "Outfit, sans-serif" }}
                  >
                    {s.value}
                  </p>
                  <p className="text-[10px] sm:text-xs md:text-sm lg:text-base uppercase tracking-wider text-slate-500 mt-1 md:mt-3 font-semibold leading-tight">
                    {s.label}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Mission — Expanding Access to Better Care */}
      <section className="zt-section border-t border-slate-200" style={{ background: "var(--zt-bg-alt)" }}>
        <div className="zt-container max-w-5xl mx-auto">
          <p className="zt-overline mb-4">Mission</p>
          <h2 className="text-2xl md:text-4xl font-bold leading-tight mb-6 md:mb-8">
            Expanding Access to Better Care
          </h2>
          <img
            src="https://customer-assets-v7afamib.emergentagent.net/job_corp-web-launch/artifacts/b111n27w_11108%20-%20ZTW%20Altus%20Stock%20Session%202026.webp"
            alt="ZT Health clinical and administrative team at Altus Emergency Centers"
            className="w-full h-[220px] sm:h-[280px] md:h-[340px] lg:h-[400px] object-cover mb-6 md:mb-8"
            loading="lazy"
            data-testid="mission-team-photo"
          />
          <p className="text-base md:text-lg text-slate-600 leading-relaxed max-w-3xl">
            Our mission is to expand access to high-quality, community-based healthcare through operational excellence, physician empowerment, and compassionate care.
          </p>
        </div>
      </section>

      {/* Texas footprint visual */}
      <section
        data-testid="about-footprint"
        className="zt-section border-t border-slate-200"
        style={{ background: "var(--zt-bg-alt)" }}
      >
        <div className="zt-container grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-center">
          <div className="lg:col-span-5">
            <p className="zt-overline mb-4">Our Texas Footprint</p>
            <h2 className="text-2xl md:text-4xl lg:text-5xl font-bold tracking-tight leading-tight mb-5">
              27 Emergency Centers & Micro-Hospitals Across Texas
            </h2>
            <p className="text-base md:text-lg text-slate-600 leading-relaxed">
              ZT Health operates a portfolio of trusted healthcare brands, including Neighbors, STAT Specialty Hospital, Exceptional Emergency Center, Express ER, Altus Emergency Center, and Altus Lumberton Hospital.
            </p>
            <Link
              to="/locations"
              data-testid="footprint-see-locations"
              className="zt-btn-primary inline-flex items-center gap-2 mt-6"
            >
              See Locations <ArrowRight size={16} />
            </Link>
          </div>
          <div className="lg:col-span-7">
            <img
              src={IMAGES.altusTexasMap}
              alt="Altus Community Healthcare Texas map"
              className="w-full h-auto"
              data-testid="about-texas-map"
            />
          </div>
        </div>
      </section>

      <CtaBand />
    </div>
  );
}
